import { Box, Paper, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Science, Assignment, BarChart } from '@mui/icons-material';
import type {
  AtterbergContext,
  AtterbergGeneral,
  AtterbergDetail,
} from '../../../types/atterberg';
import { AtterbergParametersTab } from './AtterbergParametersTab';
import { AtterbergResultEntry } from './AtterbergResultEntry';
import { AtterbergAnalysis } from './AtterbergAnalysis';
import { SectionMiniNav } from './SectionMiniNav';

interface Props {
  context: AtterbergContext;
  general: AtterbergGeneral;
  llTrials: AtterbergDetail[];
  plTrials: AtterbergDetail[];
  retestLLTrials: AtterbergDetail[];
  onGeneralChange: (field: keyof AtterbergGeneral, value: string | number | boolean | null) => void;
  onLLTrialsChange: (trials: AtterbergDetail[]) => void;
  onPLTrialsChange: (trials: AtterbergDetail[]) => void;
  onRetestLLTrialsChange: (trials: AtterbergDetail[]) => void;
  readOnly?: boolean;
  workOrderDetailKey?: string;
}

const sections = [
  { id: 'test-parameters', label: 'Test Parameters' },
  { id: 'result-entry', label: 'Result Entry' },
  { id: 'analysis-plots', label: 'Analysis & Plots' },
];

export const AtterbergSinglePageView = ({
  context, general, llTrials, plTrials, retestLLTrials,
  onGeneralChange, onLLTrialsChange, onPLTrialsChange, onRetestLLTrialsChange,
  readOnly = false, workOrderDetailKey,
}: Props) => {
  const theme = useTheme();

  const sectionHeaderSx = {
    mt: 3,
    mb: 1,
    pb: 0.5,
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    scrollMarginTop: 80,
  };

  return (
    <>
      <Box sx={{ pr: { xs: 0, lg: '180px' }, transition: 'padding 0.2s ease' }}>
        <Box id="test-parameters" sx={{ ...sectionHeaderSx, mt: 0 }}>
          <Science sx={{ color: 'primary.main', fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
            Test Parameters
          </Typography>
        </Box>
        <AtterbergParametersTab data={general} onChange={onGeneralChange} readOnly={readOnly} workOrderDetailKey={workOrderDetailKey} />

        <Box id="result-entry" sx={sectionHeaderSx}>
          <Assignment sx={{ color: 'primary.main', fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
            Result Entry
          </Typography>
        </Box>
        <AtterbergResultEntry
          general={general} llTrials={llTrials} plTrials={plTrials}
          retestLLTrials={retestLLTrials}
          onGeneralChange={onGeneralChange}
          onLLTrialsChange={onLLTrialsChange}
          onPLTrialsChange={onPLTrialsChange}
          onRetestLLTrialsChange={onRetestLLTrialsChange}
          readOnly={readOnly}
        />

        <Box id="analysis-plots" sx={sectionHeaderSx}>
          <BarChart sx={{ color: 'primary.main', fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
            Analysis & Plots
          </Typography>
        </Box>
        <AtterbergAnalysis
          context={context} general={general}
          llTrials={llTrials} plTrials={plTrials}
          retestLLTrials={retestLLTrials}
        />

        <Paper elevation={0} sx={{
          mt: 2, p: 1, textAlign: 'center',
          backgroundColor: alpha(theme.palette.divider, 0.06),
          borderRadius: 1,
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            Single-page view • Switch to tabs in the header to use the classic layout.
          </Typography>
        </Paper>
      </Box>
      <SectionMiniNav sections={sections} />
    </>
  );
};
